import { useSelector } from "react-redux";
import {
  selectBacktestingLoadingStates,
  selectFilteredBacktestingRunsData,
} from "@shared/slices/backtesting.slice";
import {
  BacktestingRunType,
  BacktestingSort,
  BacktestingStatus,
  BotType,
  SortingOrder,
} from "@shared/types";
import { backtestingFilterTypeToTitle } from "@shared/features/bots/constants";
import { useMemo, useState } from "react";
import { getSortedDataBySortSelect } from "@shared/utils/general";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { useGetBacktestingRuns } from "@shared/features/bots/BotForm/Backtesting/hooks";
import {
  dcaBotBacktestingFilters,
  signalsBotBacktestingFilters,
} from "@shared/slices/filters.slice";

interface useBacktestingPageProps {
  searchValue: string;
  botType?: BotType;
}

const useBacktestingPage = ({
  searchValue,
  botType,
}: useBacktestingPageProps) => {
  let displayCurrency = useSelector(selectDisplayCurrency);

  const args = useMemo(
    () => ({
      bot_type: botType,
    }),
    [botType]
  );
  const filters =
    botType === BotType.SIGNAL
      ? signalsBotBacktestingFilters
      : dcaBotBacktestingFilters;
  const { currentData, isLoading, isFetching, refetch } =
    useGetBacktestingRuns(args);
  const backtestingRunsData = useSelector(
    selectFilteredBacktestingRunsData(args)
  );

  const [backtestingToDelete, setBacktestingToDelete] =
    useState<BacktestingRunType>();
  const [sortBy, setSortBy] = useState<BacktestingSort>();
  const [sortDirection, setSortDirection] = useState<SortingOrder>(
    SortingOrder.DESCEND
  );

  const filteredBacktestingRuns = useMemo(() => {
    return backtestingRunsData?.filter((backtesting) =>
      backtesting.name?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, backtestingRunsData]);

  const BACKTESTING_SORT_TO_PATH = {
    [BacktestingSort.NAME]: "name",
    [BacktestingSort.CREATED]: "created_at",
    [BacktestingSort.GROUP]: "sub_group_name",
    [BacktestingSort.NET_PNL]: `net_pnl.${displayCurrency}`,
    [BacktestingSort.STATUS]: "status",
    [BacktestingSort.TIME_FRAME]: "backtesting_time_range.end",
  };

  const sortByCreatedData = (a: BacktestingRunType, b: BacktestingRunType) =>
    b.created_at - a.created_at;

  const sortByStatus = (a: BacktestingRunType, b: BacktestingRunType) => {
    return (
      +(b.status === BacktestingStatus.IN_PROGRESS) -
      +(a.status === BacktestingStatus.IN_PROGRESS)
    );
  };

  let backtestingRuns = useMemo(() => {
    if (sortBy) {
      return getSortedDataBySortSelect<BacktestingRunType, BacktestingSort>(
        filteredBacktestingRuns || [],
        sortBy,
        sortDirection,
        BACKTESTING_SORT_TO_PATH,
        STRING_SORTS,
        undefined,
        undefined,
        {
          [BacktestingSort.GROUP]: sortByCreatedData,
          [BacktestingSort.STATUS]: sortByCreatedData,
          [BacktestingSort.TIME_FRAME]: sortByCreatedData,
        }
      );
    }
    return filteredBacktestingRuns
      .slice()
      .sort(sortByCreatedData)
      .sort(sortByStatus);
  }, [sortBy, sortDirection, filteredBacktestingRuns]);

  const filtersData = {
    actions: filters.actions,
    selectors: filters.selectors,
    filterTypeToTitle: backtestingFilterTypeToTitle,
    loadingStatesSelector: selectBacktestingLoadingStates(undefined),
  };

  const onRefresh = () => {
    try {
      refetch();
    } catch {}
  };

  return {
    isLoading,
    isFetching,
    backtestingRuns,
    backtestingRunsData,
    filtersData,
    backtestingToDelete,
    setBacktestingToDelete,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    onRefresh,
    currentData,
  };
};

export default useBacktestingPage;

const STRING_SORTS = [BacktestingSort.GROUP, BacktestingSort.NAME];
